import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, BookOpen, Share2, Copy, Check } from 'lucide-react';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const DevotionPage = () => {
  const { month, day } = useParams();
  const navigate = useNavigate();
  const [devotion, setDevotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(new Date().getMonth());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const url = month && day
      ? `${BACKEND_URL}/api/devotions/${month}/${day}`
      : `${BACKEND_URL}/api/devotions/today`;

    fetch(url).then(r => {
      if (!r.ok) throw new Error('Devotion not available for this date');
      return r.json();
    }).then(data => {
      setDevotion(data);
      setPickerMonth(parseInt(data.month) - 1);
    }).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [month, day]);

  const goToDate = (m, d) => {
    navigate(`/devotions/${m}/${d}`);
    setShowPicker(false);
  };

  const handlePrev = () => {
    if (!devotion) return;
    let m = parseInt(devotion.month);
    let d = parseInt(devotion.day) - 1;
    if (d < 1) { m--; d = m >= 1 ? DAYS_IN_MONTH[m - 1] : 31; }
    if (m < 1) { m = 12; d = 31; }
    goToDate(m, d);
  };

  const handleNext = () => {
    if (!devotion) return;
    let m = parseInt(devotion.month);
    let d = parseInt(devotion.day) + 1;
    if (d > DAYS_IN_MONTH[m - 1]) { m++; d = 1; }
    if (m > 12) { m = 1; d = 1; }
    goToDate(m, d);
  };

  const handleShare = () => {
    if (navigator.share && devotion) {
      navigator.share({
        title: `Daily Devotion - ${MONTH_NAMES[parseInt(devotion.month) - 1]} ${devotion.day}`,
        text: devotion.title,
        url: window.location.href
      });
    }
  };

  const handleCopy = () => {
    if (!devotion) return;
    const text = `${MONTH_NAMES[parseInt(devotion.month) - 1]} ${devotion.day}\n\n${devotion.title}\n\n${devotion.scripture_ref || ''}\n\n${devotion.content}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center pt-20" data-testid="devotion-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-500">Loading devotion...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center pt-20" data-testid="devotion-error">
        <div className="text-center max-w-md mx-auto p-6">
          <BookOpen className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-stone-800 mb-2">Devotion Not Available</h2>
          <p className="text-stone-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/devotions')}
            className="bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 transition-colors font-medium"
            data-testid="go-to-today-btn"
          >
            Today's Devotion
          </button>
        </div>
      </div>
    );
  }

  const displayMonth = devotion ? MONTH_NAMES[parseInt(devotion.month) - 1] : '';
  const displayDay = devotion?.day || '';

  return (
    <div className="min-h-screen bg-stone-50 pt-16" data-testid="devotion-page">
      {/* Hero */}
      <div className="bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-5 h-5 text-amber-400" />
            <span className="text-amber-400 text-sm font-semibold tracking-widest uppercase">A Word in Season</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-1" data-testid="devotion-heading">
            Daily Devotion
          </h1>
          <p className="text-stone-400 text-sm">By Bro. Philip, based on the teachings of Bro. Bakht Singh</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Date Nav Bar */}
        <div className="relative -mt-6 mb-6 z-10">
          <div className="bg-white rounded-xl shadow-lg border border-stone-200 p-3 sm:p-4" data-testid="devotion-date-bar">
            <div className="flex items-center justify-between">
              <button
                onClick={handlePrev}
                className="p-2 rounded-lg hover:bg-stone-100 text-stone-600 transition-colors"
                data-testid="prev-day-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <button
                onClick={() => setShowPicker(!showPicker)}
                className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-stone-50 transition-colors"
                data-testid="date-picker-toggle"
              >
                <Calendar className="w-5 h-5 text-amber-600" />
                <div>
                  <div className="text-lg sm:text-xl font-bold text-stone-800">
                    {displayMonth} {displayDay}
                  </div>
                </div>
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/devotions')}
                  className="text-xs bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full font-semibold hover:bg-amber-100 transition-colors hidden sm:block"
                  data-testid="today-btn"
                >
                  Today
                </button>
                <button
                  onClick={handleNext}
                  className="p-2 rounded-lg hover:bg-stone-100 text-stone-600 transition-colors"
                  data-testid="next-day-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Date Picker */}
            {showPicker && (
              <div className="mt-4 pt-4 border-t border-stone-100" data-testid="date-picker-panel">
                {/* Month selector */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => setPickerMonth(prev => prev > 0 ? prev - 1 : 11)}
                    className="p-1 rounded hover:bg-stone-100"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="font-semibold text-stone-800">{MONTH_NAMES[pickerMonth]}</span>
                  <button
                    onClick={() => setPickerMonth(prev => prev < 11 ? prev + 1 : 0)}
                    className="p-1 rounded hover:bg-stone-100"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                {/* Day grid */}
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: DAYS_IN_MONTH[pickerMonth] }, (_, i) => {
                    const d = i + 1;
                    const isActive = devotion && parseInt(devotion.month) === pickerMonth + 1 && parseInt(devotion.day) === d;
                    return (
                      <button
                        key={d}
                        onClick={() => goToDate(pickerMonth + 1, d)}
                        className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                          isActive
                            ? 'bg-amber-600 text-white shadow-sm'
                            : 'text-stone-700 hover:bg-stone-100'
                        }`}
                        data-testid={`date-picker-day-${d}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Devotion Content */}
        {devotion && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden mb-8" data-testid="devotion-content-card">
            {/* Title Area */}
            <div className="px-6 sm:px-10 pt-8 pb-6 border-b border-stone-100">
              <h2 className="text-xl sm:text-2xl font-bold text-stone-900 leading-snug mb-2 font-serif" data-testid="devotion-title">
                {devotion.title}
              </h2>
              {devotion.scripture_ref && (
                <p className="text-amber-700 font-medium italic" data-testid="devotion-scripture">
                  {devotion.scripture_ref}
                </p>
              )}
            </div>

            {/* Body */}
            <div className="px-6 sm:px-10 py-8" data-testid="devotion-body">
              <div className="prose prose-stone prose-lg max-w-none">
                {devotion.content.split('\n').map((para, i) => (
                  para.trim() ? (
                    <p key={i} className="text-stone-700 leading-relaxed mb-4 text-base sm:text-lg">
                      {para}
                    </p>
                  ) : null
                ))}
              </div>

              {devotion.prayer && (
                <div className="mt-8 p-5 bg-amber-50 rounded-xl border border-amber-100">
                  <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wider mb-2">Prayer</h3>
                  <p className="text-stone-700 italic">{devotion.prayer}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="px-6 sm:px-10 py-4 border-t border-stone-100 bg-stone-50 flex items-center gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 px-3 py-1.5 rounded-lg hover:bg-stone-100 transition-all"
                data-testid="devotion-copy-btn"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </button>
              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 px-3 py-1.5 rounded-lg hover:bg-stone-100 transition-all"
                data-testid="devotion-share-btn"
              >
                <Share2 className="w-4 h-4" />
                <span>Share</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DevotionPage;
